interface EmailSignatureProps {
  bannerUrl?: string | null
  html?: string | null
  instagramUrl?: string | null
  sitioWeb?: string | null
  whatsapp?: string | null
}

/**
 * Renderiza el pie del mail = identidad del estudio: banner + texto libre +
 * línea de contacto (Instagram, página web, WhatsApp). Usado por el handler de
 * email tras render() — el HTML que produce reemplaza el slot del EmailLayout
 * vía `injectFirmaIntoSlot`.
 *
 * La firma manuscrita y el sello del contador NO van acá: quedan solo en el PDF
 * del contrato (ORR-156 / ORR-158).
 *
 * IMPORTANTE: el `html` debe llegar YA SANITIZADO (DOMPurify en backend). Los
 * demás campos se escapan al construir los links.
 */
export function renderEmailSignatureHtml({
  bannerUrl,
  html,
  instagramUrl,
  sitioWeb,
  whatsapp,
}: EmailSignatureProps): string {
  const contactos = buildContactLinks({ instagramUrl, sitioWeb, whatsapp })
  if (!bannerUrl && !html && !contactos) return ''

  const bannerTag = isHttpUrl(bannerUrl)
    ? `<div style="margin-bottom:12px;"><img src="${escapeAttr(bannerUrl!)}" alt="Banner del estudio" style="max-height:64px;display:inline-block;" /></div>`
    : ''
  const textoTag = html
    ? `<div style="font-size:14px;color:#374151;white-space:pre-wrap;">${html}</div>`
    : ''
  const contactoTag = contactos
    ? `<div style="margin-top:8px;font-size:13px;color:#6b7280;">${contactos}</div>`
    : ''

  return `
    <div style="margin:24px 40px 0 40px;padding-top:16px;border-top:1px solid #e5e7eb;">
      ${bannerTag}
      ${textoTag}
      ${contactoTag}
    </div>
  `
}

/**
 * Construye la línea de contacto del pie como links `<a>`, en el formato ORR-174:
 *   Instagram: @handle
 *   Whatsapp: <número>
 *   <web>
 * Orden fijo Instagram → Whatsapp → web. Los valores vienen de
 * configuracion_estudio (texto plano del admin del estudio); se escapan href y
 * label para evitar inyección. Los prefijos son texto fijo seguro.
 */
function buildContactLinks({
  instagramUrl,
  sitioWeb,
  whatsapp,
}: Pick<EmailSignatureProps, 'instagramUrl' | 'sitioWeb' | 'whatsapp'>): string {
  const links = [
    buildInstagramLink(instagramUrl),
    buildWhatsappLink(whatsapp),
    buildWebLink(sitioWeb),
  ].filter(Boolean)
  return links.join('<br>')
}

function anchor(href: string, label: string): string {
  return `<a href="${escapeAttr(href)}" style="color:#059669;text-decoration:none;">${escapeAttr(label)}</a>`
}

function buildInstagramLink(value?: string | null): string {
  const v = value?.trim()
  if (!v) return ''
  const isUrl = /^https?:\/\//i.test(v)
  const href = isUrl ? v : `https://instagram.com/${v.replace(/^@/, '')}`
  const label = isUrl ? v : `@${v.replace(/^@/, '')}`
  // Marcada para que `quitarInstagramDeFirma` la saque en los mails que ya llevan el bloque de Instagram.
  return `<span data-firma-instagram="true">Instagram: ${anchor(href, label)}</span>`
}

/**
 * Línea de Instagram del pie con su `<br>` siguiente. El contenido no anida `<span>`: el label va
 * escapado, así que no puede traer un `</span>` propio.
 */
const FIRMA_INSTAGRAM_REGEX = /<span data-firma-instagram="true">[\s\S]*?<\/span>(?:<br>)?/g

/**
 * Saca la línea de Instagram del pie del estudio. La usa el bloque «Seguinos en Instagram» de los
 * mails a clientes: con el bloque arriba, la línea del pie repetía el mismo dato.
 */
export function quitarInstagramDeFirma(html: string): string {
  return html.replace(FIRMA_INSTAGRAM_REGEX, '')
}

function buildWhatsappLink(value?: string | null): string {
  const v = value?.trim()
  if (!v) return ''
  const digits = v.replace(/\D/g, '')
  if (!digits) return ''
  return `Whatsapp: ${anchor(`https://wa.me/${digits}`, v)}`
}

function buildWebLink(value?: string | null): string {
  const v = value?.trim()
  if (!v) return ''
  const href = /^https?:\/\//i.test(v) ? v : `https://${v}`
  const label = v.replace(/^https?:\/\//i, '')
  return anchor(href, label)
}

/**
 * Solo permite URLs http(s) en src de <img>. Bloquea javascript:/data:/vbscript:
 * por si una URL no proveniente del bucket entra a configuracion_estudio.
 */
function isHttpUrl(url: string | null | undefined): url is string {
  return !!url && /^https?:\/\//i.test(url)
}

export function escapeAttr(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

/**
 * Regex del slot de firma. El EmailLayout marca el slot con
 * `<Section data-signature-slot="true" />`, pero react-email renderiza
 * `<Section>` como `<table ...>` (NO `<section>`), por lo que el elemento real
 * en el HTML es `<table ... data-signature-slot="true" ...>...</table>`.
 * Matchea esa tabla completa (incluido su tbody/tr/td) hasta el primer
 * `</table>` — el slot no anida tablas. (ORR-112)
 */
export const FIRMA_SLOT_REGEX = /<table[^>]*\bdata-signature-slot="true"[^>]*>[\s\S]*?<\/table>/i

/**
 * Reemplaza el slot de firma del EmailLayout por el HTML de la firma.
 * Si `firmaHtml` es vacío (org sin firma configurada), el slot se elimina y el
 * email queda sin firma (sin dejar marcador visible). Si no hay slot, devuelve
 * el HTML intacto.
 */
export function injectFirmaIntoSlot(html: string, firmaHtml: string): string {
  // Replacer como función: evita que secuencias `$&`/`$1` dentro de `firmaHtml`
  // (firma del usuario) se interpreten como patrones de reemplazo.
  return html.replace(FIRMA_SLOT_REGEX, () => firmaHtml)
}
